// Shared static text rendering for CanvasEditor (non-editing render) AND
// PageThumbnailPreview, so the thumbnail is a scaled copy of the exact same
// render tree — no parallel implementation, no drift.
//
//  • textVisualStyle(element)  → the font/visual style block for the element box.
//  • <StaticTextBlock element> → the inner content: gradient-fill text as SVG,
//    or a flex-nested plain-HTML block (byte-identical to the editor's static
//    text so wrapping/baseline match exactly).
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getTextLines } from '../../lib/text/getTextLines';
import { applyListPrefix } from '../../lib/text/applyListPrefix';
import { gradientTextLayout } from '../../lib/text/gradientTextLayout';
import { buildSVGGradient } from './renderUtils';
import { computeFrameImageLayout } from './frameLayout';
import { effectTextStyle } from './propertyEffects';

function gradientInfo(element) {
  const hasFillEnabled = element.fillEnabled !== false;
  const fillGrad = element.fillGradient;
  const hasGradientFill = hasFillEnabled && fillGrad && fillGrad.colors && fillGrad.colors.length >= 2;
  const strokeGrad = element.strokeGradient;
  const hasGradientStroke = strokeGrad && strokeGrad.colors && strokeGrad.colors.length >= 2;
  return { hasFillEnabled, fillGrad, hasGradientFill, strokeGrad, hasGradientStroke,
    useGradientSVG: hasGradientFill || hasGradientStroke };
}

function resolveShadow(element) {
  const opacity = element.shadowOpacity ?? 1;
  const raw = element.shadowColor ?? '#000000';
  const color = opacity >= 1 ? raw : (() => {
    const m = raw.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    return m ? `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${opacity})` : raw;
  })();
  const has = (element.shadowBlur ?? 0) > 0 || (element.shadowOffsetX ?? 0) !== 0
    || (element.shadowOffsetY ?? 0) !== 0 || element.shadowColor;
  return { has, color, opacity };
}

/** Font/visual style block applied to the positioned text box. Mirrors the
 *  editor's static text box exactly (so the box is the single source of font
 *  styling that the inner content inherits). */
export function textVisualStyle(element, { isMask = false } = {}) {
  const { useGradientSVG, hasFillEnabled } = gradientInfo(element);
  const strokeWidth = Number(element.strokeWidth) || 0;
  const strokeColor = element.stroke || '#000000';
  const shadow = resolveShadow(element);
  return {
    fontSize: element.fontSize,
    fontFamily: element.fontFamily,
    color: useGradientSVG ? undefined : (isMask ? 'transparent' : (!hasFillEnabled ? 'transparent' : element.color)),
    fontWeight: element.fontWeight,
    fontStyle: element.fontStyle,
    textAlign: element.textAlign,
    textDecoration: element.textDecoration,
    letterSpacing: element.letterSpacing || 'normal',
    lineHeight: element.lineHeight || 'normal',
    ...(shadow.has ? { textShadow: `${element.shadowOffsetX ?? 0}px ${element.shadowOffsetY ?? 0}px ${element.shadowBlur ?? 4}px ${shadow.color}` } : {}),
    ...(!useGradientSVG && strokeWidth > 0 ? { WebkitTextStroke: `${strokeWidth}px ${strokeColor}` } : {}),
    // Unified text effect (outline/hollow/neon/lift/shadow) — applied LAST so it
    // wins, mirroring CanvasEditor's static text box. Keeps the thumbnail's
    // plain-text effect styling identical to the editor.
    ...effectTextStyle(element),
  };
}

/** The inner content of a text element (gradient SVG or flex-nested plain).
 *  `idScope` namespaces the SVG gradient/filter ids so an editor instance and a
 *  thumbnail instance of the same element can coexist without id collisions. */
export function StaticTextBlock({ element, idScope = '' }) {
  const text = element.text ?? element.content ?? '';
  const lines = applyListPrefix(getTextLines(text || ''), element.listType);
  const { hasGradientFill, fillGrad, hasGradientStroke, strokeGrad, useGradientSVG } = gradientInfo(element);

  if (useGradientSVG) {
    const svgFontSize = Number(String(element.fontSize || '16').replace('px', '')) || 16;
    const { lh, firstBaselineY, tx, anchor } = gradientTextLayout({
      fontSize: svgFontSize, lineHeight: element.lineHeight, width: element.width, textAlign: element.textAlign,
    });
    const strokeWidth = Number(element.strokeWidth) || 0;
    const strokeColor = element.stroke || '#000000';
    const shadow = resolveShadow(element);
    const fillId = `${idScope}text-fill-grad-${element.id}`;
    const strokeId = `${idScope}text-stroke-grad-${element.id}`;
    const shadowId = `${idScope}text-shadow-${element.id}`;
    return (
      <svg width={element.width} height={element.height} style={{ overflow: 'visible', pointerEvents: 'none' }}>
        <defs>
          {shadow.has && (
            <filter id={shadowId} x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx={element.shadowOffsetX ?? 0} dy={element.shadowOffsetY ?? 0}
                stdDeviation={(element.shadowBlur ?? 4) / 2} floodColor={shadow.color} floodOpacity={shadow.opacity} />
            </filter>
          )}
          {hasGradientFill && buildSVGGradient(fillId, fillGrad)}
          {hasGradientStroke && buildSVGGradient(strokeId, strokeGrad)}
        </defs>
        <text
          fontFamily={element.fontFamily || 'sans-serif'}
          fontWeight={element.fontWeight || 'normal'}
          fontStyle={element.fontStyle || 'normal'}
          fontSize={svgFontSize}
          textDecoration={element.textDecoration || 'none'}
          letterSpacing={element.letterSpacing && element.letterSpacing !== 'normal' ? element.letterSpacing : undefined}
          fill={hasGradientFill ? `url(#${fillId})` : (element.color || '#000000')}
          stroke={hasGradientStroke ? `url(#${strokeId})` : (strokeWidth > 0 ? strokeColor : 'none')}
          strokeWidth={strokeWidth > 0 || hasGradientStroke ? strokeWidth || 1 : undefined}
          paintOrder="stroke fill"
          textAnchor={anchor}
          filter={shadow.has ? `url(#${shadowId})` : undefined}
        >
          {lines.map((line, i) => (
            <tspan key={i} x={tx} dy={i === 0 ? firstBaselineY : lh}>{line || ' '}</tspan>
          ))}
        </text>
      </svg>
    );
  }

  return (
    <div style={{ pointerEvents: 'none', height: '100%', display: 'flex', alignItems: 'flex-start', padding: '0 8px', boxSizing: 'border-box' }}>
      <div style={{ minWidth: '20px', width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'normal', overflowWrap: 'normal', boxSizing: 'border-box' }}>
        {element.listType ? lines.join('\n') : text}
      </div>
    </div>
  );
}

// ── Images ──────────────────────────────────────────────────────────────────
// Shared image layout so CanvasEditor and PageThumbnailPreview position/scale
// images identically (cover, or crop via computeFrameImageLayout).

/** Per-corner (cornerRadii) or uniform (cornerRadius) border-radius for the box. */
export function imageBoxRadius(element) {
  const c = element.cornerRadii;
  if (c && typeof c === 'object') {
    const v = (k) => Math.max(0, Number(c[k]) || 0);
    return `${v('tl')}px ${v('tr')}px ${v('br')}px ${v('bl')}px`;
  }
  return element.cornerRadius ? `${element.cornerRadius}px` : undefined;
}

/** The <img> style — crop layout (computeFrameImageLayout) or plain cover. */
export function imageImgStyle(element) {
  const fit = element.fit || 'cover';
  const hasCrop = !!(element.cropScale && element.cropScale !== 1)
    || !!element.cropOffsetX || !!element.cropOffsetY || !!element.cropRotation || fit !== 'cover';
  const layout = hasCrop
    ? computeFrameImageLayout(
        { width: element.width, height: element.height, fit,
          image: { offsetX: element.cropOffsetX || 0, offsetY: element.cropOffsetY || 0, scale: element.cropScale || 1 } },
        element.naturalWidth, element.naturalHeight)
    : null;
  return layout
    ? { position: 'absolute', left: layout.left, top: layout.top, width: layout.width, height: layout.height,
        transform: `rotate(${element.cropRotation || 0}deg)`, maxWidth: 'none', display: 'block' }
    : { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
}

/** Shared QR rendering (same value/size/level/colors in editor and thumbnail). */
export function StaticQR({ element }) {
  const size = Math.max(1, Math.min(element.width, element.height) - 16);
  return (
    <QRCodeSVG
      value={element.data || 'https://example.com'}
      size={size}
      fgColor={element.color || '#000000'}
      bgColor={element.bgColor || '#ffffff'}
      level="M"
      style={{ width: '100%', height: '100%' }}
    />
  );
}
